import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold tracking-[-0.005em] ring-offset-background transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-brand-500 text-white shadow-[0_1px_0_rgba(255,255,255,0.2)_inset,0_1px_2px_hsl(225_50%_11%/0.08)] hover:bg-brand-400',
        destructive:
          'bg-[hsl(var(--error))] text-[hsl(var(--error-foreground))] shadow-xs hover:bg-[hsl(var(--error)/0.9)]',
        outline:
          'border border-border bg-card text-foreground shadow-xs hover:bg-muted',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/85',
        ghost: 'text-foreground hover:bg-muted',
        link: 'h-auto px-0 text-brand-700 underline underline-offset-[3px] hover:text-brand-600',
        soft: 'bg-brand-50 text-brand-700 hover:bg-brand-100',
        inverse: 'bg-ink-1000 text-white hover:bg-ink-800',
      },
      size: {
        default: 'h-10 px-4 text-sm min-h-[44px] md:min-h-[40px]',
        sm: 'h-[28px] px-3 text-[13px] min-h-[44px] md:min-h-[28px]',
        lg: 'h-12 px-5 text-[15px] min-h-[48px]',
        icon: 'h-10 w-10 min-h-[44px] min-w-[44px] md:min-h-[40px] md:min-w-[40px]',
        touch: 'h-12 px-6 py-3 min-h-[48px] text-base',
        'touch-safe': 'h-16 px-8 py-4 min-h-[64px] text-lg font-semibold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
